import React, { useEffect, useRef, useState, useCallback } from "react";

const MODOS = {
  painel: [
    { id: "angulo", label: "📐 Ângulo", dica: "Encaixe o corte INTEIRO dentro do quadro, de frente, e alinhe com a guia de 33°.", overlay: true, moldura: { w: 74, h: 58 } },
    { id: "ferimentos", label: "🔪 Ferimentos", dica: "Aproxime a 30–50 cm: encha o quadro com a linha de corte e a casca de regeneração.", moldura: { w: 78, h: 44 } },
    { id: "consumo", label: "📏 Consumo", dica: "Painel completo na vertical dentro do quadro, com marcas de gabarito se houver. Use os traços-guia para comparar as faixas.", moldura: { w: 52, h: 80 }, tracos: 4 },
    { id: "faixa", label: "📏 Faixa de corte", dica: "Aproxime a 30–40 cm: encaixe a faixa consumida (linhas de corte acima do corte atual) entre os traços-guia, nítida para contar linha a linha.", moldura: { w: 80, h: 48 }, tracos: 5 },
    { id: "equipamentos", label: "🥤 Equipamentos", dica: "Bica, caneca e arame dentro do quadro.", moldura: { w: 64, h: 56 } },
    { id: "geral", label: "🌳 Geral", dica: "Painel/tronco inteiro dentro do quadro, luz natural.", moldura: { w: 60, h: 78 } },
  ],
  folhagem: [
    { id: "folha", label: "🍃 Folha", dica: "Encha o quadro com a folha e o sintoma visível.", moldura: { w: 72, h: 54, oval: true } },
    { id: "conjunto", label: "🌿 Conjunto", dica: "Ramo/copa com várias folhas dentro do quadro.", moldura: { w: 82, h: 58 } },
  ],
};

export default function CameraGuiada({ tipo, onCapture, onClose, modoInicial }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const checkCanvasRef = useRef(document.createElement("canvas"));
  const modos = MODOS[tipo] || MODOS.painel;
  const [modo, setModo] = useState(modos.find((m) => m.id === modoInicial) || modos[0]);
  const [erro, setErro] = useState(false);
  const [espelhado, setEspelhado] = useState(false);
  const [nivel, setNivel] = useState(null);
  const [anguloGuia, setAnguloGuia] = useState(33);
  const [checks, setChecks] = useState({ luz: false, foco: false, quadro: false });

  useEffect(() => {
    let ativo = true;
    if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
      DeviceOrientationEvent.requestPermission().catch(() => {});
    }
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 } }, audio: false })
      .then((s) => {
        if (!ativo) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      })
      .catch(() => setErro(true));
    const h = (e) => { if (e.beta != null) setNivel({ beta: e.beta, gamma: e.gamma }); };
    window.addEventListener("deviceorientation", h);
    return () => {
      ativo = false;
      window.removeEventListener("deviceorientation", h);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const analisarFrame = useCallback(() => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = checkCanvasRef.current;
    const W = 120, H = Math.max(40, Math.round((120 * v.videoHeight) / v.videoWidth));
    c.width = W; c.height = H;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(v, 0, 0, W, H);
    const data = ctx.getImageData(0, 0, W, H).data;
    const g = new Float32Array(W * H);
    let soma = 0;
    for (let i = 0; i < W * H; i++) {
      const l = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
      g[i] = l; soma += l;
    }
    const luma = soma / (W * H);
    const m = modo.moldura;
    const mx0 = Math.round((W * (50 - m.w / 2)) / 100), mx1 = Math.round((W * (50 + m.w / 2)) / 100);
    const my0 = Math.round((H * (50 - m.h / 2)) / 100), my1 = Math.round((H * (50 + m.h / 2)) / 100);
    let grad = 0, n = 0, gradQ = 0, nQ = 0;
    for (let y = 1; y < H - 1; y++) {
      for (let x = 1; x < W - 1; x++) {
        const i = y * W + x;
        const gv = Math.abs(g[i + 1] - g[i - 1]) + Math.abs(g[i + W] - g[i - W]);
        grad += gv; n++;
        if (x >= mx0 && x < mx1 && y >= my0 && y < my1) { gradQ += gv; nQ++; }
      }
    }
    setChecks({
      luz: luma > 45 && luma < 215,
      foco: n > 0 && grad / n > 7,
      quadro: nQ > 0 && gradQ / nQ > 7,
    });
  }, [modo]);

  useEffect(() => {
    const t = setInterval(analisarFrame, 700);
    return () => clearInterval(t);
  }, [analisarFrame]);

  const nivelado = nivel && Math.abs(nivel.gamma) < 5 && Math.abs(nivel.beta - 90) < 10;
  const exigeNivel = modo.overlay && nivel != null;
  const pronto = checks.luz && checks.foco && checks.quadro && (!exigeNivel || nivelado);

  const clamp = (v, lim) => Math.max(-lim, Math.min(lim, v));
  const desvioX = nivel ? clamp(nivel.gamma, 25) : 0;
  const desvioY = nivel ? clamp(nivel.beta - 90, 25) : 0;
  let dicaNivel = "";
  if (nivel && !nivelado) {
    if (Math.abs(desvioY) >= Math.abs(desvioX)) {
      dicaNivel = desvioY > 0 ? "⬇️ Abaixe o topo do celular" : "⬆️ Levante o topo do celular";
    } else {
      dicaNivel = desvioX > 0 ? "↺ Gire para a esquerda" : "↻ Gire para a direita";
    }
  }

  const capturar = (forcar = false) => {
    if (!pronto && !forcar) return;
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const max = 1280;
    const scale = Math.min(1, max / Math.max(v.videoWidth, v.videoHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(v.videoWidth * scale);
    c.height = Math.round(v.videoHeight * scale);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    onCapture(c.toDataURL("image/jpeg", 0.85), { ...modo, anguloMedido: modo.overlay ? anguloGuia : null });
  };

  const chip = (ok, label, testid) => (
    <span className={`text-[10px] px-2 py-1 rounded-full font-semibold ${ok ? "bg-green-500 text-white" : "bg-white/20 text-white/80"}`} data-testid={testid}>
      {ok ? "✓" : "•"} {label}
    </span>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="camera-guiada">
      <div className="flex items-center justify-between p-3 bg-black/80 gap-2">
        <button onClick={onClose} className="text-white text-sm font-semibold px-3 py-1.5 rounded-full bg-white/20" data-testid="camera-fechar-btn">✕ Fechar</button>
        <div className="flex gap-1.5 flex-wrap justify-end" data-testid="camera-checks">
          {nivel != null && chip(nivelado, "Nível", "camera-check-nivel")}
          {chip(checks.luz, "Luz", "camera-check-luz")}
          {chip(checks.foco, "Foco", "camera-check-foco")}
          {chip(checks.quadro, "No quadro", "camera-check-quadro")}
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden">
        {erro ? (
          <div className="h-full flex items-center justify-center p-6 text-center">
            <div className="text-white text-sm" data-testid="camera-erro">
              ⚠️ Não foi possível abrir a câmera aqui.<br /><br />
              Verifique a permissão de câmera do navegador, ou use o botão <strong>🖼️ Galeria</strong> — no celular, a galeria também oferece a opção "Tirar foto".
            </div>
          </div>
        ) : (
          <>
            <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div
                className={`relative border-[3px] transition-colors ${modo.moldura.oval ? "rounded-full" : "rounded-3xl"} ${pronto ? "border-green-400 shadow-[0_0_24px_rgba(74,222,128,0.6)]" : "border-white/60"}`}
                style={{ width: `${modo.moldura.w}%`, height: `${modo.moldura.h}%` }}
                data-testid="camera-moldura"
              >
                <span className={`absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-bold px-3 py-1 rounded-full ${pronto ? "bg-green-500 text-white" : "bg-black/60 text-white"}`} data-testid="camera-moldura-status">
                  {pronto ? "✓ Enquadrado — pode fotografar" : "Encaixe no quadro"}
                </span>
                {modo.tracos && (
                  <div className="absolute inset-0 pointer-events-none" data-testid="camera-tracos">
                    {Array.from({ length: modo.tracos }, (_, i) => (
                      <div key={i} className="absolute left-[4%] right-[4%] border-t border-dashed border-cyan-300/70" style={{ top: `${((i + 1) * 100) / (modo.tracos + 1)}%` }} />
                    ))}
                    <span className="absolute bottom-1 right-2 text-[9px] font-semibold text-cyan-200 bg-black/40 px-1.5 py-0.5 rounded">traços-guia p/ faixas</span>
                  </div>
                )}
                {modo.overlay && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative w-[92%]">
                      <div className="border-t-2 border-dashed border-white/40 w-full" />
                      <div className={`absolute left-0 right-0 top-0 border-t-[3px] border-dashed ${anguloGuia >= 30 && anguloGuia <= 36 ? "border-green-400" : "border-yellow-400"}`} style={{ transform: `rotate(${espelhado ? -anguloGuia : anguloGuia}deg)` }} />
                      <span className={`absolute -top-7 left-1/2 -translate-x-1/2 text-xs font-bold bg-black/50 px-2 py-0.5 rounded ${anguloGuia >= 30 && anguloGuia <= 36 ? "text-green-400" : "text-yellow-400"}`} data-testid="camera-angulo-valor">📐 {anguloGuia}°</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {modo.overlay && (
              <button onClick={() => setEspelhado(!espelhado)} className="absolute top-3 right-3 text-white text-xs px-3 py-1.5 rounded-full bg-white/20" data-testid="camera-espelhar-btn">↔️ Inverter guia</button>
            )}
            {nivel != null && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 pointer-events-none" data-testid="camera-bolha-nivel">
                <div className={`relative w-16 h-16 rounded-full border-2 bg-black/40 transition-colors ${nivelado ? "border-green-400" : "border-white/50"}`}>
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full border-2 border-white/40" />
                  <div className="absolute left-1/2 top-1/2 w-px h-full -translate-x-1/2 bg-white/15" />
                  <div className="absolute top-1/2 left-0 h-px w-full -translate-y-1/2 bg-white/15" />
                  <div
                    className={`absolute w-5 h-5 rounded-full transition-all duration-150 ${nivelado ? "bg-green-400" : "bg-amber-400"}`}
                    style={{ left: `calc(50% + ${(desvioX * 22) / 25}px - 10px)`, top: `calc(50% + ${(desvioY * 22) / 25}px - 10px)` }}
                    data-testid="camera-bolha"
                  />
                </div>
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${nivelado ? "bg-green-500 text-white" : "bg-black/60 text-amber-300"}`} data-testid="camera-nivel-dica">
                  {nivelado ? "✓ Nivelado" : dicaNivel}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      <div className="bg-black/90 p-3 space-y-2">
        {modo.overlay && !erro && (
          <div className="flex items-center gap-2" data-testid="camera-angulo-ajuste">
            <span className="text-white/80 text-[11px] whitespace-nowrap">Gire a guia sobre o corte:</span>
            <input type="range" min="10" max="60" step="1" value={anguloGuia} onChange={(e) => setAnguloGuia(Number(e.target.value))} className="flex-1 accent-yellow-400" data-testid="camera-angulo-slider" />
            <button onClick={() => setAnguloGuia(33)} className="text-[11px] text-white px-2 py-1 rounded-full bg-white/15" data-testid="camera-angulo-reset">33°</button>
          </div>
        )}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {modos.map((m) => (
            <button key={m.id} onClick={() => setModo(m)}
              className={`whitespace-nowrap text-xs px-3 py-1.5 rounded-full font-semibold ${modo.id === m.id ? "bg-green-500 text-white" : "bg-white/15 text-white"}`}
              data-testid={`camera-modo-${m.id}`}>{m.label}</button>
          ))}
        </div>
        <p className="text-white/80 text-xs" data-testid="camera-dica">💡 {modo.dica}</p>
        {!erro && (
          <div className="flex flex-col items-center pt-1 gap-1">
            <button
              onClick={() => capturar(false)}
              disabled={!pronto}
              className={`w-16 h-16 rounded-full border-4 transition-all ${pronto ? "bg-white border-green-400 animate-pulse active:scale-95" : "bg-white/30 border-white/30 cursor-not-allowed"}`}
              data-testid="camera-capturar-btn"
              aria-label="Tirar foto"
            />
            {!pronto && (
              <button onClick={() => capturar(true)} className="text-white/50 text-[10px] underline" data-testid="camera-forcar-btn">capturar mesmo assim</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
